import { writeBatch } from 'firebase/firestore';
import { createRefPath_event } from 'global/firestoreData/appData/appRefPaths';
import { SportsSeasonContext } from 'projectSpecific/sports/cnr/contexts/SportsSeasonContext';
import { seasonalHelpers } from 'projectSpecific/sports/cnr/reducers/reducerHelpers/seasonalHelpers';
import { useSeasonalData } from 'projectSpecific/sports/useHelpers/useSeasonals';
import { useCallback, useContext, useState } from 'react';
import { DatabaseContext } from '../DatabaseContext';
import { ParentContext } from '../ParentContext';
import { _cacheGroupTypes } from './useTimestamp';

const _allowRssFeederUpdate = true
const _allowRssFeedsUpdate = true

export const useRssFeed = () => {

  const parentContext = useContext(ParentContext);
  const { states, fn_cls } = parentContext ?? {}
  const { getFeed } = fn_cls ?? {}
  const { app_state, paps_state } = states ?? {}
  const { pathViews } = paps_state ?? {}
  const { mainDb } = app_state ?? {}

  const databaseContext = useContext(DatabaseContext);
  const { database_fns } = databaseContext ?? {};

  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state } = sportsSeasonContext ?? {}
  const { currentSeason_info } = sportsSeason_state ?? {}
  const { sportsKey, _sportsAbr } = currentSeason_info ?? {}

  const _sdh = useSeasonalData()

  const [updating, setUpdating] = useState()

  const getRssFeeds = useCallback(async ({ currentRssFeeders, callback }) => {
    const results = await Promise.all(
      _.map(currentRssFeeders, (feed, feedKey) =>
        getRssFeed({
          selectedFeed: feedKey,
          url: feed.baseUrl,
        })
      )
    )
    const rssFeeds = _.flatten(results)
    updateRssFeeds({ rssFeeds })
    callback(rssFeeds)
  }, []);

  const getRssFeed = useCallback(async ({
    selectedFeed,
    url,
    titleSearch,
    callback
  }) => {
    const { result, success } = await getFeed(url)

    if (!success) {
      callback?.([])
      return []
    }

    const { items, events } = result ?? {}

    if (items) {
      const _items = _.map(items, item => normalizeRssItem(item))

      if (titleSearch) {
        _items = _items.filter(item =>
          item.title?.toLowerCase().includes(titleSearch.toLowerCase())
        )
      }

      const _items_filtered = _.filter(_items, item =>
        _.includes(item?.searchText || '', 'high-school') ||
        _.includes(item?.searchText || '', 'high school') ||
        _.includes(item?.searchText || '', 'highschool') ||
        _.includes(item?.searchText || '', 'sports')
      )

      const _items_picked = _.map(_items_filtered, item => ({
        ..._.pick(item, [
          'author',
          'categories',
          'content',
          'contentSnippet',
          'creator',
          'enclosures',
          // 'generator',
          'guid',
          'link',
          'pubDate',
          'title',
        ]),
        selectedFeed,
        _itemKey: encodeURIComponent(
          `${selectedFeed}_${item.guid}`
        ).replace(/\./g, '%2E')
      }))

      callback?.(_items_picked)

      return _items_picked
    }

    if (events) {
      // your events processing...

      callback?.([])
      return []
    }

    callback?.([])
    return []
  }, [])

  const updateRssFeeds = useCallback(async ({ rssFeeds }) => {

    const isSport = false// pathViews.sports ? true : false

    const _collectionName = '_rssFeeds'

    const cb = () => setUpdating(false)

    setUpdating(true)

    const _pks = isSport ? seasonalHelpers.getPks(pathViews, null, sportsKey, ['teams']) : seasonalHelpers.getPks(pathViews, null, sportsKey, ['sports', 'teams'])

    const _sportsYearCollectionItem = await _sdh.getSports_collectionItem(_pks, _collectionName, true)

    const batch = writeBatch(mainDb);

    const items = createFeedItems(rssFeeds, database_fns)

    const _timestamp = Date.now()

    if (_.isEmpty(_sportsYearCollectionItem)) {
      // add a NEW doc
      const docKey = database_fns.get_docKey()
      const newDocKey = isSport ? `${_sportsAbr}_${docKey}` : docKey
      const refPath = createRefPath_event(pathViews, [_collectionName, newDocKey])
      const dtu = {
        parentKeys: _pks,
        _timestamp,
        rssFeeds: { ...items }

      }
      database_fns.set_doc(refPath, dtu, false, null, batch); // Assuming doc_update returns a promise
    } else {
      const { _itemKey } = _sportsYearCollectionItem ?? {}
      const refPath = createRefPath_event(pathViews, [_collectionName, _itemKey])
      const dtu = {
        rssFeeds: {},
        _timestamp
      }
      _.forEach(items, item => {
        dtu[`rssFeeds.${item._itemKey}`] = item
      })
      database_fns.update_doc(refPath, dtu, null, batch); // Assuming doc_update returns a promise
    }

    if (_allowRssFeedsUpdate) {
      database_fns.update_timestamp(_cacheGroupTypes.rssFeedInfo, pathViews.districts, _timestamp, batch)
      await batch.commit()
      cb()
    } else {
      cb()
    }

  }, []);

  const updateRssFeeders = useCallback(async ({ rssFeeder }) => {

    const isSport = pathViews.sports ? true : false

    const _collectionName = '_rssFeeders'
    const _key = 'rssFeeders'

    const cb = () => setUpdating(false)

    setUpdating(true)

    const _pks = isSport ? seasonalHelpers.getPks(pathViews, null, sportsKey) : seasonalHelpers.getPks(pathViews, null, sportsKey, ['sports'])

    const _sportsYearCollectionItem = await _sdh.getSports_collectionItem(_pks, _collectionName, true)

    const batch = writeBatch(mainDb);

    if (_.isEmpty(_sportsYearCollectionItem)) {
      // add a NEW doc
      const docKey = database_fns.get_docKey()
      const newDocKey = isSport ? `${_sportsAbr}_${docKey}` : docKey
      const refPath = createRefPath_event(pathViews, [_collectionName, newDocKey])
      const dtu = {
        parentKeys: _pks,
        [_key]: {
          [rssFeeder._itemKey]: rssFeeder
        }
      }
      database_fns.set_doc(refPath, dtu, false, null, batch); // Assuming doc_update returns a promise
    } else {
      const { _itemKey } = _sportsYearCollectionItem ?? {}
      const refPath = createRefPath_event(pathViews, [_collectionName, _itemKey])
      const dtu = {
        [`${_key}.${rssFeeder._itemKey}`]: rssFeeder
      }
      database_fns.update_doc(refPath, dtu, null, batch); // Assuming doc_update returns a promise
    }

    if (_allowRssFeederUpdate) {
      const _timestamp = Date.now()
      database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonInfo, pathViews.sports, _timestamp, batch)
      await batch.commit()
      cb()
    } else {
      cb()
    }


  }, []);

  return { getRssFeeds, getRssFeed, updateRssFeeds, updateRssFeeders, updating }

}

const extractUniqueTeams = (events) => {
  return _.chain(events)
    .map("name") // get the `name` values
    .flatMap(name => name.split(" at ")) // split into [Team1, Team2]
    .map(team => team.trim()) // clean up whitespace
    .uniq() // ensure uniqueness
    .value(); // return array
};

const createFeedItems = (items) => {
  const _feedItems = {}
  _.forEach(items, item => {
    _feedItems[item._itemKey] = item
  })
  return _feedItems
}


const generateFeedKey = (feed) => {
  const { link, title } = feed;
  const baseKey = _.kebabCase(`${link}-${title}`).replace(/[/.$#[\]]/g, "-");
  return baseKey
};

const normalizeRssItem = (item) => {
  const getLink = () => {
    if (typeof item.link === 'string') return item.link

    if (Array.isArray(item.link)) {
      const alternate =
        item.link.find(link => link?.rel === 'alternate') ||
        item.link.find(link => link?.href)

      return alternate?.href || ''
    }

    if (item.link?.href) return item.link.href

    return ''
  }

  const getCategories = () => {
    if (!item.categories) return []

    if (Array.isArray(item.categories)) {
      return item.categories
        .map(category => {
          if (typeof category === 'string') return category
          return category?.term || category?.label || ''
        })
        .filter(Boolean)
    }

    return [item.categories]
  }

  const getAuthor = () => {
    if (typeof item.author === 'string') return item.author

    if (item.author?.name) return item.author.name

    if (typeof item.creator === 'string') return item.creator

    if (typeof item['dc:creator'] === 'string') {
      return item['dc:creator']
    }

    return ''
  }

  const link = getLink()
  const categories = getCategories()

  const title = item.title || ''
  const description = item.description || item.summary || ''
  const content = item.content || ''
  const contentSnippet = item.contentSnippet

  const searchText = _.toLower([
    title,
    link,
    description,
    content,
    ...categories
  ]
    .filter(Boolean)
    .join(' '))

  return {
    title,
    link,

    guid: item.guid || item.id || '',

    pubDate:
      item.pubDate ||
      item.published ||
      item.updated ||
      '',

    description,
    content,
    contentSnippet,
    categories,

    author: getAuthor(),

    enclosures: item.enclosures || item.enclosure || null,

    searchText
  }
}